/**
 * Time Utilities
 *
 * Shared time and date manipulation functions for planning and calendar domains.
 * These utilities are framework-agnostic and safe to use in both backend and frontend code.
 */

function parseDateKeyUtc(dayKey: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!match) return null;

  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;

  const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  if (Number.isNaN(date.getTime())) return null;
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return date;
}

function toUtcDateKey(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a date to ISO date string (YYYY-MM-DD) using LOCAL timezone.
 */
export function toLocalDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert time string (HH:MM) to minutes since midnight.
 */
export function timeToMinutes(time?: string): number | null {
  const parsed = parseTimeString(time ?? '');
  if (!parsed) return null;
  return parsed.hours * 60 + parsed.minutes;
}

/**
 * Calculate duration in minutes between two HH:MM values.
 * Handles overnight ranges (end < start) by wrapping to next day.
 * Returns null when either input is invalid.
 */
export function timeRangeDurationMinutes(start: string, end: string): number | null {
  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);
  if (startMinutes === null || endMinutes === null) return null;
  return endMinutes >= startMinutes ? endMinutes - startMinutes : 24 * 60 - startMinutes + endMinutes;
}

/**
 * Calculate duration in hours between two HH:MM values.
 * Returns null when either input is invalid.
 */
export function timeRangeDurationHours(start: string, end: string): number | null {
  const minutes = timeRangeDurationMinutes(start, end);
  if (minutes === null) return null;
  return minutes / 60;
}

/**
 * Convert minutes since midnight to HH:MM.
 * Invalid or negative values fall back to "00:00".
 */
export function minutesToTime(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return '00:00';
  const wholeMinutes = Math.floor(minutes);
  const hours = Math.floor(wholeMinutes / 60);
  const mins = wholeMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * Get the day key for the previous day.
 */
export function getPreviousDayKey(dayKey: string): string | null {
  const date = parseDateKeyUtc(dayKey);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() - 1);
  return toUtcDateKey(date);
}

/**
 * Get the day key for the next day.
 */
export function getNextDayKey(dayKey: string): string | null {
  const date = parseDateKeyUtc(dayKey);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + 1);
  return toUtcDateKey(date);
}

/** Accepted input types for {@link formatTime}. */
export type TimeValue = string | Date | null | undefined;

/**
 * Format time from ISO string or Date to HH:mm (24-hour format).
 */
export function formatTime(timeValue: TimeValue): string {
  if (!timeValue) return '--:--';

  const date = timeValue instanceof Date ? timeValue : new Date(timeValue);
  if (Number.isNaN(date.getTime())) return '--:--';

  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Format time from ISO string or Date to HH:mm (24-hour format) using UTC.
 * Use this for timezone-neutral template times (e.g., shift unit definitions).
 */
export function formatTimeUtc(timeValue: TimeValue): string {
  return formatTime(timeValue);
}

/**
 * Format a date/time value to HH:mm using UTC.
 * Returns undefined when value is empty or invalid.
 */
export function extractTimeUtcOrUndefined(timeValue: TimeValue): string | undefined {
  const formatted = formatTimeUtc(timeValue);
  return formatted === '--:--' ? undefined : formatted;
}

/**
 * Extract hour from a time value.
 */
export function getHourFromTime(time: string | Date): number | null {
  if (!time) return null;

  if (time instanceof Date) {
    return time.getUTCHours();
  }

  const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (timeMatch) {
    return Number.parseInt(timeMatch[1], 10);
  }

  const date = new Date(time);
  if (!Number.isNaN(date.getTime())) {
    return date.getUTCHours();
  }

  return null;
}

/**
 * Extract hour from a time value using UTC.
 * Use this for timezone-neutral template times (e.g., shift unit definitions).
 */
export function getHourFromTimeUtc(time: string | Date): number | null {
  return getHourFromTime(time);
}

/**
 * Parse an ISO time string to Date object.
 */
export function parseISOTime(time: string): Date | null {
  if (!time) return null;

  const date = new Date(time);
  if (Number.isNaN(date.getTime())) return null;

  return date;
}

/**
 * Format a time range for display (e.g., "08:00-16:00").
 */
export function formatTimeRange(startTime: string | Date | null | undefined, endTime: string | Date | null | undefined): string {
  return `${formatTime(startTime)}-${formatTime(endTime)}`;
}

/**
 * Format a UTC time range for grid display.
 * Returns placeholder when either side is invalid.
 */
export function formatTimeRangeUtcOrPlaceholder(
  startTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined,
  placeholder = '--:-- - --:--'
): string {
  const start = formatTimeUtc(startTime);
  const end = formatTimeUtc(endTime);
  return start !== '--:--' && end !== '--:--' ? `${start}-${end}` : placeholder;
}

/**
 * Get ISO week number for a date.
 */
export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Check if a date is today.
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * Parse a time string (HH:MM) to hours and minutes.
 */
export function parseTimeString(time: string): { hours: number; minutes: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!match) return null;
  const hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

/**
 * Build an ISO UTC timestamp on a fixed reference date from an HH:MM value.
 * Returns null when the input cannot be parsed.
 */
export function toReferenceIsoTime(time: string, referenceDate = '1970-01-01'): string | null {
  const parsed = parseTimeString(time);
  if (!parsed) return null;
  const normalizedHours = String(parsed.hours).padStart(2, '0');
  const normalizedMinutes = String(parsed.minutes).padStart(2, '0');
  return `${referenceDate}T${normalizedHours}:${normalizedMinutes}:00Z`;
}

/**
 * Convert a YYYY-MM-DD date key to ISO weekday (0=Monday..6=Sunday).
 * Returns null for invalid or overflow dates.
 */
export function isoWeekdayFromDateKey(dateKey: string): number | null {
  const date = parseDateKeyUtc(dateKey);
  if (!date) return null;

  const jsWeekday = date.getUTCDay(); // 0=Sunday..6=Saturday
  return jsWeekday === 0 ? 6 : jsWeekday - 1; // 0=Monday..6=Sunday
}
